import type { editor as monacoEditor, IRange, Selection } from 'monaco-editor';

const BOLD: MarkerPair = { before: '**', after: '**' };
const ITALIC: MarkerPair = { before: '_', after: '_' };
const BOLD_ITALIC: MarkerPair = { before: '***', after: '***' };

interface MarkerPair {
  before: string;
  after: string;
}

function getSelectionRange(editor: monacoEditor.IStandaloneCodeEditor) {
  const model = editor.getModel();
  const selection = editor.getSelection();
  if (!model || !selection) return null;
  return { model, selection };
}

function offsetAt(model: monacoEditor.ITextModel, line: number, column: number) {
  return model.getOffsetAt({ lineNumber: line, column });
}

function rangeFromOffsets(
  model: monacoEditor.ITextModel,
  start: number,
  end: number,
): IRange {
  const startPos = model.getPositionAt(start);
  const endPos = model.getPositionAt(end);
  return {
    startLineNumber: startPos.lineNumber,
    startColumn: startPos.column,
    endLineNumber: endPos.lineNumber,
    endColumn: endPos.column,
  };
}

function applyEdit(
  editor: monacoEditor.IStandaloneCodeEditor,
  range: IRange,
  text: string,
  newSelection?: IRange,
): void {
  editor.executeEdits('format', [{ range, text, forceMoveMarkers: true }]);
  if (newSelection) {
    editor.setSelection(newSelection);
  }
  editor.focus();
}

function isWrappedIn(text: string, marker: MarkerPair): boolean {
  return (
    text.startsWith(marker.before)
    && text.endsWith(marker.after)
    && text.length >= marker.before.length + marker.after.length
  );
}

function unwrap(text: string, marker: MarkerPair): string {
  return text.slice(marker.before.length, text.length - marker.after.length);
}

function getSurroundingMarker(
  model: monacoEditor.ITextModel,
  startOffset: number,
  endOffset: number,
): MarkerPair | null {
  const value = model.getValue();
  const markers = [BOLD_ITALIC, BOLD, ITALIC];
  for (const marker of markers) {
    if (
      value.slice(startOffset - marker.before.length, startOffset) === marker.before
      && value.slice(endOffset, endOffset + marker.after.length) === marker.after
    ) {
      return marker;
    }
  }
  return null;
}

function replaceWrappedText(
  editor: monacoEditor.IStandaloneCodeEditor,
  model: monacoEditor.ITextModel,
  selection: Selection,
  selected: string,
  startOffset: number,
  endOffset: number,
  marker: MarkerPair,
  nextText: string,
): void {
  if (isWrappedIn(selected, marker)) {
    applyEdit(editor, selection, nextText);
    return;
  }

  const range = rangeFromOffsets(
    model,
    startOffset - marker.before.length,
    endOffset + marker.after.length,
  );
  applyEdit(editor, range, nextText);
}

function toggleInlineStyle(
  editor: monacoEditor.IStandaloneCodeEditor,
  style: 'bold' | 'italic',
  placeholder: string,
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const selected = model.getValueInRange(selection);
  const startOffset = offsetAt(model, selection.startLineNumber, selection.startColumn);
  const endOffset = offsetAt(model, selection.endLineNumber, selection.endColumn);

  if (selected.length === 0) {
    const marker = style === 'bold' ? BOLD : ITALIC;
    const insert = marker.before + placeholder + marker.after;
    applyEdit(editor, selection, insert, rangeFromOffsets(
      model,
      startOffset + marker.before.length,
      startOffset + marker.before.length + placeholder.length,
    ));
    return;
  }

  const surrounding = getSurroundingMarker(model, startOffset, endOffset);
  const activeMarker = surrounding
    ?? (isWrappedIn(selected, BOLD_ITALIC) ? BOLD_ITALIC
      : isWrappedIn(selected, BOLD) ? BOLD
        : isWrappedIn(selected, ITALIC) ? ITALIC
          : null);

  const inner = activeMarker
    ? (surrounding ? selected : unwrap(selected, activeMarker))
    : selected;

  if (style === 'bold') {
    if (activeMarker === BOLD_ITALIC) {
      replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, BOLD_ITALIC, ITALIC.before + inner + ITALIC.after);
      return;
    }
    if (activeMarker === BOLD) {
      replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, BOLD, inner);
      return;
    }
    if (activeMarker === ITALIC) {
      replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, ITALIC, BOLD_ITALIC.before + inner + BOLD_ITALIC.after);
      return;
    }
    applyEdit(editor, selection, BOLD.before + selected + BOLD.after);
    return;
  }

  if (activeMarker === BOLD_ITALIC) {
    replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, BOLD_ITALIC, BOLD.before + inner + BOLD.after);
    return;
  }
  if (activeMarker === ITALIC) {
    replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, ITALIC, inner);
    return;
  }
  if (activeMarker === BOLD) {
    replaceWrappedText(editor, model, selection, selected, startOffset, endOffset, BOLD, BOLD_ITALIC.before + inner + BOLD_ITALIC.after);
    return;
  }
  applyEdit(editor, selection, ITALIC.before + selected + ITALIC.after);
}

export function toggleBoldSelection(editor: monacoEditor.IStandaloneCodeEditor): void {
  toggleInlineStyle(editor, 'bold', 'bold text');
}

export function toggleItalicSelection(editor: monacoEditor.IStandaloneCodeEditor): void {
  toggleInlineStyle(editor, 'italic', 'italic text');
}

export function toggleWrapSelection(
  editor: monacoEditor.IStandaloneCodeEditor,
  marker: MarkerPair,
  placeholder = 'text',
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const selected = model.getValueInRange(selection);
  const startOffset = offsetAt(model, selection.startLineNumber, selection.startColumn);
  const endOffset = offsetAt(model, selection.endLineNumber, selection.endColumn);

  if (selected.length === 0) {
    const insert = marker.before + placeholder + marker.after;
    applyEdit(editor, selection, insert, rangeFromOffsets(
      model,
      startOffset + marker.before.length,
      startOffset + marker.before.length + placeholder.length,
    ));
    return;
  }

  if (isWrappedIn(selected, marker)) {
    applyEdit(editor, selection, unwrap(selected, marker));
    return;
  }

  const surrounding = getSurroundingMarker(model, startOffset, endOffset);
  if (surrounding?.before === marker.before) {
    applyEdit(editor, rangeFromOffsets(
      model,
      startOffset - marker.before.length,
      endOffset + marker.after.length,
    ), selected);
    return;
  }

  applyEdit(editor, selection, marker.before + selected + marker.after);
}

export function toggleInlineCodeSelection(editor: monacoEditor.IStandaloneCodeEditor): void {
  toggleWrapSelection(editor, { before: '`', after: '`' }, 'code');
}

const LINK_FULL_PATTERN = /^\[(.*)\]\([^)]*\)$/;
const LINK_AFTER_PATTERN = /^\]\([^)]*\)/;

export function toggleLinkSelection(
  editor: monacoEditor.IStandaloneCodeEditor,
  placeholder = 'link text',
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const selected = model.getValueInRange(selection);

  if (selected.length === 0) {
    const startOffset = offsetAt(model, selection.startLineNumber, selection.startColumn);
    const insert = `[${placeholder}](https://)`;
    applyEdit(editor, selection, insert, rangeFromOffsets(
      model,
      startOffset + 1,
      startOffset + 1 + placeholder.length,
    ));
    return;
  }

  const fullMatch = selected.match(LINK_FULL_PATTERN);
  if (fullMatch) {
    applyEdit(editor, selection, fullMatch[1]);
    return;
  }

  const startOffset = offsetAt(model, selection.startLineNumber, selection.startColumn);
  const endOffset = offsetAt(model, selection.endLineNumber, selection.endColumn);
  const value = model.getValue();
  const hasBracketBefore = value[startOffset - 1] === '[';
  const afterMatch = value.slice(endOffset).match(LINK_AFTER_PATTERN);

  if (hasBracketBefore && afterMatch) {
    const range = rangeFromOffsets(model, startOffset - 1, endOffset + afterMatch[0].length);
    applyEdit(editor, range, selected);
    return;
  }

  applyEdit(editor, selection, `[${selected}](https://)`);
}

export function toggleHeading(
  editor: monacoEditor.IStandaloneCodeEditor,
  level: 1 | 2 | 3,
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const prefix = `${'#'.repeat(level)} `;
  const headingPattern = /^#{1,6}\s+/;
  const listPattern = /^(- |\d+\.\s+|>\s+)/;
  const startLine = selection.startLineNumber;
  const endLine = selection.endLineNumber;
  const edits: monacoEditor.IIdentifiedSingleEditOperation[] = [];

  for (let line = startLine; line <= endLine; line += 1) {
    const lineContent = model.getLineContent(line);
    const body = lineContent.replace(headingPattern, '').replace(listPattern, '');

    if (lineContent.startsWith(prefix)) {
      edits.push({
        range: {
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: lineContent.length + 1,
        },
        text: body,
        forceMoveMarkers: true,
      });
    } else {
      edits.push({
        range: {
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: lineContent.length + 1,
        },
        text: prefix + body,
        forceMoveMarkers: true,
      });
    }
  }

  editor.executeEdits('format', edits);
  editor.focus();
}

const NUMBERED_LIST_PATTERN = /^\d+\.\s+/;

export function prefixLines(
  editor: monacoEditor.IStandaloneCodeEditor,
  prefix: string,
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const startLine = selection.startLineNumber;
  const endLine = selection.endLineNumber;
  const listPattern = /^(- |\d+\.\s+|>\s+)/;
  const isNumberedPrefix = NUMBERED_LIST_PATTERN.test(prefix);
  const edits: monacoEditor.IIdentifiedSingleEditOperation[] = [];

  for (let line = startLine; line <= endLine; line += 1) {
    const lineContent = model.getLineContent(line);
    let body = lineContent;

    if (prefix.startsWith('#')) {
      body = body.replace(/^#{1,6}\s+/, '');
    }
    body = body.replace(listPattern, '');

    const alreadyPrefixed = isNumberedPrefix
      ? NUMBERED_LIST_PATTERN.test(lineContent)
      : lineContent.startsWith(prefix);

    if (alreadyPrefixed) {
      edits.push({
        range: {
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: lineContent.length + 1,
        },
        text: body,
        forceMoveMarkers: true,
      });
    } else {
      edits.push({
        range: {
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: lineContent.length + 1,
        },
        text: prefix + body,
        forceMoveMarkers: true,
      });
    }
  }

  editor.executeEdits('format', edits);
  editor.focus();
}

export function insertCodeBlock(editor: monacoEditor.IStandaloneCodeEditor): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  const { model, selection } = ctx;
  const selected = model.getValueInRange(selection);

  if (selected.trim()) {
    toggleWrapSelection(editor, { before: '```\n', after: '\n```' }, selected);
    return;
  }

  applyEdit(
    editor,
    selection,
    '```\n\n```',
    {
      startLineNumber: selection.startLineNumber + 1,
      startColumn: 1,
      endLineNumber: selection.startLineNumber + 1,
      endColumn: 1,
    },
  );
}

export function insertAtCursor(
  editor: monacoEditor.IStandaloneCodeEditor,
  text: string,
): void {
  const ctx = getSelectionRange(editor);
  if (!ctx) return;

  applyEdit(editor, ctx.selection, text);
}

export function wrapSelection(
  editor: monacoEditor.IStandaloneCodeEditor,
  before: string,
  after: string,
  placeholder = 'text',
): void {
  toggleWrapSelection(editor, { before, after }, placeholder);
}
